/** @file GuideSheetDialog: the overpaint guide of a facing, to save as guide.<deg>.png with its brief. */
import { encodePng } from '@zilo/pose3d-image-io/png/encodePng';
import { webZlibCodec } from '@zilo/pose3d-image-io/zlib/webZlibCodec';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { ArtModal } from '../ArtModal';
import { saveDownload } from '../saveDownload';
import { SHEET_FACINGS } from './sheetFacings';
import type { GuideSheet } from './sheetTypes';
import { useGuide } from './useGuide';
import { useImageUrls } from './useImageUrls';

/** The guide loader of the sheet flow, the brief copy (guide mode) and close. */
export type GuideSheetDialogProps = { loadGuide: (deg: number) => Promise<GuideSheet>; onCopyBrief: () => void; onClose: () => void };

const BUTTON = { padding: '3px 10px', fontSize: 12 };
const SELECT = { fontSize: 12, background: '#15171b', color: '#e6e8ec', border: '1px solid #44474f', borderRadius: 4 };

/** Saves the guide image as `guide.<deg>.png` (a PNG blob URL, revoked after the click). */
async function saveGuide(guide: GuideSheet): Promise<void> {
  const png = await encodePng(guide.image, { codec: webZlibCodec });
  const url = URL.createObjectURL(new Blob([new Uint8Array(png)], { type: 'image/png' }));
  saveDownload(`guide.${String(guide.placement.facingDeg)}.png`, { url });
  setTimeout(() => { URL.revokeObjectURL(url); }, 1000);
}

/** The facing select, the guide (shown 2x, pixelated) with its legend, and Save / Copy brief / Close. */
export function GuideSheetDialog({ loadGuide, onCopyBrief, onClose }: GuideSheetDialogProps): ReactElement {
  const [facing, setFacing] = useState(0);
  const { guide, error } = useGuide(loadGuide, facing);
  const [url] = useImageUrls(useMemo(() => (guide === null ? [] : [guide.image]), [guide]));
  return (
    <ArtModal label="Guide sheet" wide>
      <strong>Guide sheet: paint the character over it, then import it with Overpaint sheet</strong>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        Facing
        <select aria-label="Guide facing" value={facing} style={SELECT} onChange={(e) => { setFacing(Number(e.target.value)); }}>
          {SHEET_FACINGS.map((f) => <option key={f.deg} value={f.deg}>{f.label} {f.deg}</option>)}
        </select>
        {guide === null ? null : <span data-testid="guide-size">{guide.image.width}x{guide.image.height} px, {guide.regions.length} regions</span>}
      </label>
      <div style={{ overflow: 'auto', background: 'repeating-conic-gradient(#2a2d33 0% 25%, #202227 0% 50%) 0 0 / 16px 16px', padding: 8 }}>
        {error !== null ? <span role="alert">Guide: {error}</span> : null}
        {url === undefined || url === '' || guide === null ? null : (
          <img src={url} alt={`guide ${String(facing)}`} style={{ width: guide.image.width * 2, imageRendering: 'pixelated', display: 'block' }} />
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button type="button" style={BUTTON} onClick={onCopyBrief}>Copy guide brief</button>
        <button type="button" style={BUTTON} disabled={guide === null} onClick={() => { if (guide !== null) void saveGuide(guide); }}>Save guide.{facing}.png</button>
        <button type="button" style={BUTTON} onClick={onClose}>Close</button>
      </div>
    </ArtModal>
  );
}
